import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { NavArrowButton } from "@/components/ui/nav-arrow-button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

export interface PaginationBarProps {
  page: number;
  limit: number;
  total?: number;
  totalCountCapped?: boolean;
  limitOptions?: number[];
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  // Show the page-size selector. Off hides it (fixed page size).
  showPageSize?: boolean;
  className?: string;
}

// Full-width pagination bar pinned at the bottom of a page (top divider spans
// edge-to-edge). Controls are grouped and left-aligned per the design: page
// size, prev/next, then the range summary. Reusable across list pages — pass
// the same paging props any table already tracks.
//
// Place it OUTSIDE the page's horizontal padding so the divider is full width;
// the bar supplies its own inner padding.
export const PaginationBar = ({
  page,
  limit,
  total = 0,
  totalCountCapped = false,
  limitOptions = [10, 20, 50, 100],
  onPageChange,
  onLimitChange,
  showPageSize = true,
  className,
}: PaginationBarProps): React.ReactElement => {
  const { t } = useTranslation();

  const handlePrevPage = (): void => {
    if (page > 0) onPageChange(page - 1);
  };

  const handleNextPage = (): void => {
    if (total > (page + 1) * limit) onPageChange(page + 1);
  };

  const start = Math.min(page * limit + 1, total);
  const end = Math.min((page + 1) * limit, total);

  return (
    <div
      className={cn(
        "flex w-full items-center gap-3 border-t px-6 py-3",
        className
      )}
    >
      {showPageSize && (
        <Select
          value={limit.toString()}
          onValueChange={(value) => onLimitChange?.(Number(value))}
        >
          <SelectTrigger size="sm" className="w-auto gap-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {limitOptions.map((n) => (
              <SelectItem key={n} value={n.toString()}>
                {t("common.perPage", { count: n, defaultValue: `${n} / page` })}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="flex items-center">
        <NavArrowButton
          direction="prev"
          label={t("common.previousPage")}
          onClick={handlePrevPage}
          disabled={page === 0}
        />
        <NavArrowButton
          direction="next"
          label={t("common.nextPage")}
          onClick={handleNextPage}
          disabled={total <= (page + 1) * limit}
        />
      </div>

      <span className="text-sm text-muted-foreground">
        {t(
          totalCountCapped
            ? "common.showingRange_capped"
            : "common.showingRange",
          { start, end, total }
        )}
      </span>
    </div>
  );
};
